<template>
  <v-app>
    <v-app-bar app color="primary" dark dense>
      <!-- Logo and Title -->
      <v-toolbar-title class="d-flex align-center py-0 mr-0">
        <router-link to="/" class="d-flex align-center text-decoration-none white--text">
          <v-img
            :src="logoSmall"
            max-height="28"
            max-width="28"
            min-height="28"
            min-width="28"
            height="28"
            width="28"
            contain
            class="mr-1 app-logo mobile-logo"
            eager
            :aspect-ratio="1"
          >
            <template v-slot:placeholder>
              <v-icon color="white">mdi-shopping</v-icon>
            </template>
          </v-img>
          <span class="font-weight-medium hidden-xs-only">RightBuy.me</span>
        </router-link>
      </v-toolbar-title>

      <!-- About Link with reduced margin on mobile -->
      <v-btn
        text
        to="/about"
        class="ml-2 ml-sm-8 px-1 px-sm-2"
        small
      >
        <v-icon small left>mdi-information</v-icon>
        <span class="hidden-xs-only">About</span>
      </v-btn>

      <v-spacer></v-spacer>

      <!-- Search button on desktop -->
      <v-btn
        icon
        class="hidden-sm-and-down"
        @click="$router.push('/search')"
        small
      >
        <v-icon small>mdi-magnify</v-icon>
      </v-btn>

      <!-- Simple Install button - icon only -->
      <v-btn
        v-if="canInstallApp"
        icon
        class="ml-1"
        @click="installApp"
        title="Install app"
        small
      >
        <v-icon small>mdi-arrow-down-circle</v-icon>
      </v-btn>

      <!-- Ethics Control -->
      <ethics-control />

      <!-- Dark mode toggle -->
      <v-btn icon @click="toggleDarkMode" small class="ml-1 mr-1">
        <v-icon small>{{ isDarkMode ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
      </v-btn>

      <!-- Auth Section -->
      <user-auth-status @user-upgraded="handleUserUpgraded" />
    </v-app-bar>

    <v-main>
      <v-container fluid>
        <v-fade-transition mode="out-in">
          <template v-if="!loading">
            <router-view></router-view>
          </template>
          <v-skeleton-loader
            v-else
            type="article"
            class="mx-auto"
          ></v-skeleton-loader>
        </v-fade-transition>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import EthicsControl from '@/components/EthicsControl.vue';
import UserAuthStatus from '@/components/UserAuthStatus.vue';
import logoSmall from './assets/logo_small.png';
import logo from './assets/logo.png';

export default {
  name: 'App',
  components: {
    EthicsControl,
    UserAuthStatus
  },
  data() {
    return {
      loading: true,
      logoSmall: logoSmall,
      logo: logo
    };
  },
  computed: {
    isAuthenticated() {
      return this.$store.getters['auth/isAuthenticated'];
    },
    user() {
      return this.$store.getters['auth/getUser'];
    },
    userInitials() {
      if (!this.user || !this.user.displayName) return '?';
      return this.user.displayName
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },
    isDarkMode() {
      return this.$store.getters['theme/isDarkMode'];
    },
    canInstallApp() {
      return this.$store.getters['pwa/canInstall'];
    }
  },
  methods: {
    async handleLogout() {
      try {
        await this.$store.dispatch('auth/logout');
        if (this.$route.path !== '/') {
          this.$router.push('/');
        }
      } catch (error) {
        console.error('Logout failed:', error);
      }
    },
    handleUserUpgraded() {
      // Refresh user preferences or perform any actions needed after account upgrade
      this.$store.dispatch('preferences/loadPreferences');
    },
    async initializeAuth() {
      try {
        await this.$store.dispatch('auth/initAuth');
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        this.loading = false;
      }
    },
    toggleDarkMode() {
      this.$store.commit('theme/toggleDarkMode');
    },
    // Simplified PWA installation method - just show the prompt when icon is clicked
    installApp() {
      this.$store.dispatch('pwa/showInstallPrompt');
    }
  },
  created() {
    this.initializeAuth();
  }
};
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  line-height: 1.6;
  color: #333;
}

.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.loading-spinner {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.nav-brand {
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
  text-decoration: none;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.nav-link {
  color: #666;
  text-decoration: none;
  font-size: 0.9rem;
}

.login-button {
  background-color: transparent;
  color: #1976d2;
  border: 1px solid #1976d2;
}

.login-button:hover {
  background-color: #f5f5f5;
  text-decoration: none;
}

.register-button {
  background-color: #1976d2;
  color: white;
}

.register-button:hover {
  background-color: #1565c0;
  text-decoration: none;
}

a {
  color: #1976d2;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

.text-decoration-none {
  text-decoration: none;
}

/* Add new styles for logo integration */
.v-toolbar__title {
  display: flex !important;
  align-items: center !important;
  min-width: 40px !important;
}

.v-toolbar__title .v-img {
  transition: transform 0.2s ease;
  min-width: 32px;
  min-height: 32px;
  display: block !important;
  position: relative !important;
  z-index: 5 !important;
}

.v-toolbar__title:hover .v-img {
  transform: scale(1.1);
}

/* Ensure the logo is visible on all devices */
.app-logo {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  position: relative !important;
  z-index: 5 !important;
}

/* Style for login button */
.login-btn {
  background-color: rgba(255, 255, 255, 0.15) !important;
  border-radius: 50% !important;
  margin-left: 4px !important;
}

/* Mobile specific adjustments */
@media (max-width: 600px) {
  .v-toolbar__title {
    margin-right: 0 !important;
    min-width: 28px !important;
    width: auto !important;
    overflow: visible !important;
    padding-right: 0 !important;
  }
  
  .app-logo {
    margin-right: 0 !important;
    display: block !important;
    position: relative !important;
    z-index: 5 !important;
  }
  
  .mobile-logo {
    display: inline-block !important;
    visibility: visible !important;
    opacity: 1 !important;
    width: 28px !important;
    height: 28px !important;
  }
  
  .v-toolbar__title .v-responsive__content {
    width: 100% !important;
    height: 100% !important;
  }
  
  /* Reduce spacing in app bar for mobile */
  .v-app-bar .v-btn {
    margin-left: 2px !important;
    margin-right: 2px !important;
    padding: 0 4px !important;
  }
  
  /* Ensure login button is visible */
  .v-app-bar .v-btn--text {
    min-width: 0 !important;
    padding: 0 4px !important;
  }
}
</style>
